import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Last line of defense: a render crash shows a readable error card instead of
 * a white screen. (Pre-2026-10-29 a missing .env threw in AuthContext and the
 * whole app went blank.)
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[nMachCRM] render error:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div
        role="alert"
        style={{
          maxWidth: 640,
          margin: '10vh auto',
          padding: 32,
          background: '#FFFDF9',
          border: '1px solid #E4DDD2',
          borderRadius: 12,
          fontFamily: 'Inter, system-ui, sans-serif',
          color: '#0B1B33',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 12,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#9E3F26',
          }}
        >
          nMachCRM · render error
        </p>
        <h1 style={{ fontSize: 25, lineHeight: 1.3, margin: '8px 0 16px' }}>Something went wrong.</h1>
        <p style={{ color: '#5A6472', margin: '0 0 8px' }}>{this.state.error.message}</p>
        <pre
          style={{
            background: '#EFEAE2',
            padding: 12,
            borderRadius: 8,
            fontSize: 12,
            overflow: 'auto',
            maxHeight: 240,
          }}
        >
          {this.state.error.stack}
        </pre>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          style={{
            marginTop: 16,
            height: 40,
            padding: '0 16px',
            background: '#9E3F26',
            color: '#FFFFFF',
            border: 0,
            borderRadius: 8,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    )
  }
}
